// useFetchWithAuth.tsx
import { useCallback, useContext, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useLocation } from 'react-router-dom';
import { useOrgRoleData } from 'core/state/useOrgRoleData';
import { ErrorContext } from 'core/context/ErrorContext';

interface FetchOptions extends RequestInit {
  headers?: HeadersInit;
}

const isLoginRequiredError = (error: unknown): boolean => {
  if (!error || typeof error !== 'object') {
    return false;
  }
  const authError = error as { error?: string; message?: string };
  const code = authError.error || '';
  const message = authError.message || '';
  return (
    code === 'login_required' ||
    code === 'consent_required' ||
    message.includes('login_required') ||
    message.includes('consent_required')
  );
};

export const useFetchWithAuth = () => {
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [orgRoleData] = useOrgRoleData();
  const { setError } = useContext(ErrorContext);
  const location = useLocation();
  const reauthStarted = useRef(false);

  // Keep latest values in a ref so getData can stay referentially stable.
  // Unstable getData in useEffect deps has caused infinite refetch loops
  // (e.g. compliance-summary on Sites & Pages).
  const latestRef = useRef({
    getAccessTokenSilently,
    loginWithRedirect,
    orgRoleData,
    setError,
    location,
  });
  latestRef.current = {
    getAccessTokenSilently,
    loginWithRedirect,
    orgRoleData,
    setError,
    location,
  };

  const getData = useCallback(
    async (
      url: string,
      options: FetchOptions = {},
      overrideGenericErrorHandling?: boolean,
      useBasicAuth?: boolean,
      orgId?: string,
    ): Promise<Response> => {
      const {
        getAccessTokenSilently: getToken,
        loginWithRedirect: loginRedirect,
        orgRoleData: roleData,
        setError: showError,
        location: loc,
      } = latestRef.current;

      try {
        // Fetch access token
        let authHeader;
        if (useBasicAuth) {
          const USERNAME = 'APSKC_GLOBAL_ADMIN';
          const PASSWORD = 'APSKC123$';
          authHeader = `Basic ${btoa(`${USERNAME}:${PASSWORD}`)}`;
        } else {
          const token = await getToken();
          authHeader = `Bearer ${token}`;
        }

        // Perform the fetch request
        const isFormData =
          typeof FormData !== 'undefined' && options.body instanceof FormData;
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            Authorization: authHeader,
            orgId:
              orgId ||
              roleData?.org?.id ||
              JSON.parse(localStorage.getItem('orgRoleData'))?.org?.id,
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
          },
        });
        // Handle errors
        if (!response.ok && !overrideGenericErrorHandling) {
          let errorMessage;
          if (response.status === 400) {
            errorMessage = response.statusText;
            try {
              const errorData = await response.json();
              errorMessage = errorData.message || errorMessage;
            } catch (e) {
              // Ignore JSON parsing errors
            }
          } else {
            errorMessage =
              'We have encountered an issue. Please try again later.';
          }

          showError({
            status: response.status,
            message: errorMessage,
          });
          throw new Error(errorMessage);
        }

        return response;
      } catch (error) {
        if (isLoginRequiredError(error) && !reauthStarted.current) {
          reauthStarted.current = true;
          void loginRedirect({
            appState: {
              returnTo: loc.pathname + loc.search,
            },
          });
        }
        console.error('Error fetching data:', error);
        // setError({ message: (error as Error).message });
        throw error;
      }
    },
    [],
  );

  return { getData };
};
